/**
 * Error handling utilities
 */
import parse from 'html-react-parser';
import React from 'react';
import { getPermissionDeniedReason } from '@/features/access-control/types/role';
import { API_CONFIG } from '@/config/api';

const validErrorCodes = ['US', 'WS', 'CT', 'CL', 'GE', 'DG', 'QH', 'QE', 'CN'];

const getDocUrl = (errorCode: string): string => {
  const code = errorCode.slice(0, 2);
  switch (code) {
    case 'US':
      return API_CONFIG.DOC_BASE_URL + 'error-codes/user-account-errors';
    case 'WS':
      return API_CONFIG.DOC_BASE_URL + 'error-codes/workspace-errors';
    case 'CT':
      return API_CONFIG.DOC_BASE_URL + 'error-codes/catalog-errors';
    case 'CL':
      return API_CONFIG.DOC_BASE_URL + 'error-codes/cluster-errors';
    case 'GE':
      return API_CONFIG.DOC_BASE_URL + 'error-codes/general-errors';
    case 'DG':
      return API_CONFIG.DOC_BASE_URL + 'error-codes/data-governance-errors';
    case 'QH':
      return API_CONFIG.DOC_BASE_URL + 'error-codes/query-history-errors';
    case 'QE':
      return API_CONFIG.DOC_BASE_URL + 'error-codes/query-editor-errors';
    case 'CN':
      return API_CONFIG.DOC_BASE_URL + 'error-codes/connectivity-errors';
    default:
      return API_CONFIG.DOC_BASE_URL + 'error-codes';
  }
};

/**
 * Parses tooltip title with error code links
 */
export const parseTooltipTitle = (
  title: string | React.ReactNode,
  variant?: string
): React.ReactNode => {
  if (typeof title !== 'string') {
    return title;
  }
  const [errorCode = ''] = title.includes(':') ? title.split(':') : [];
  const getError = (err: string): string | React.ReactNode => {
    let error: string | React.ReactNode;
    try {
      error = parse(err);
    } catch {
      error = err;
    }
    return error;
  };

  if (title && errorCode && validErrorCodes.includes(errorCode.slice(0, 2))) {
    return React.createElement(
      'span',
      null,
      React.createElement(
        'a',
        {
          rel: 'noreferrer',
          href: getDocUrl(errorCode) + '#' + errorCode?.toLowerCase(),
          target: '_blank',
          className: variant === 'dark' ? 'text-white' : 'text-danger',
          style: { textDecoration: 'underline' },
        },
        errorCode === 'schema' ? 'Error' : errorCode
      ),
      ' : ',
      getError(title.replace(`${errorCode}:`, ''))
    );
  } else {
    return getError(title);
  }
};

interface ErrorResponse {
  response?: {
    data?: {
      result?: Record<string, string[]> | string;
      message?: string;
      detail?: string;
    };
  };
  result?: Record<string, string[]> | string;
  message?: string;
  detail?: string;
}

/**
 * Parses API errors into user-friendly messages
 */
export const parseError = (
  error: ErrorResponse = {},
  key: string = '',
  isWarning: boolean = false
): React.ReactNode | string => {
  try {
    if (!error) {
      return 'Something went wrong';
    }
    const { result, detail } = error?.response?.data || error;
    let { message } = error?.response?.data || error;

    if (key && typeof result === 'object' && result !== null) {
      message = (result as Record<string, string[]>)[key]?.[0];
    } else {
      message = message || (result as string);
    }

    const [errorCode = ''] =
      message && (message as string).includes(':') ? (message as string).split(':') : [];

    const renderLink = (code: string, msg: string): React.ReactNode => {
      return React.createElement(
        'span',
        null,
        React.createElement(
          'a',
          {
            rel: 'noreferrer',
            href: getDocUrl(code) + '#' + code?.toLowerCase(),
            target: '_blank',
            className: `${isWarning ? 'text-warning-info' : 'text-danger'} `,
            style: { textDecoration: 'underline' },
          },
          code === 'schema' ? 'Error' : code
        ),
        ': ',
        msg?.replace(`${code}:`, '')
      );
    };

    if (message && errorCode) {
      return renderLink(errorCode, message as string);
    } else if (
      typeof result === 'object' &&
      !Array.isArray(result) &&
      result !== null &&
      Object.keys(result).length
    ) {
      const resultObj = (error?.response?.data?.result ?? error?.result) as
        | Record<string, string[]>
        | undefined;
      const key = Object.keys(resultObj ?? {})[0] ?? '';
      return React.createElement('span', null, ' ', resultObj?.[key]?.[0]);
    } else if (typeof message === 'string') {
      return React.createElement('span', null, message);
    } else if (typeof detail === 'string' && detail) {
      return React.createElement('span', null, detail);
    } else {
      return React.createElement('span', null, 'Something went wrong');
    }
  } catch {
    return 'Something went wrong. Please try again!';
  }
};

/**
 * Handles HTTP error codes
 */
export const handleErrorCode = (error: {
  response?: { status?: number; config?: { url?: string } };
}): void => {
  if ([401].includes(error?.response?.status || 0)) {
    // Token expiry handling - commented out for now
  } else if ([403].includes(error?.response?.status || 0)) {
    // Not redirecting on 403 and not showing toast
  } else if (
    [502, 503].includes(error?.response?.status || 0) &&
    window.location.pathname !== '/service-unavailable'
  ) {
    if (error?.response?.config?.url?.includes('/endpoints/socket/')) {
      return;
    } else {
      window.location.href = '/service-unavailable';
    }
  }
};

/**
 * Returns a user-facing permission-denied message when an API error is a 403,
 * otherwise null. The message reuses the shared denial phrasing so it stays
 * uniform with the disabled-action tooltips rendered by PermissionGate.
 *
 * Reusable by any cluster-action handler that needs to surface a forbidden
 * response (resume, suspend, scale, …) — pass the matching resource/action.
 */
export const getPermissionErrorMessage = (
  error: { response?: { status?: number } } | undefined,
  resource: string,
  action: string
): string | null => {
  if (error?.response?.status !== 403) {
    return null;
  }
  return getPermissionDeniedReason(resource, action);
};

/**
 * Backend error code returned when an action can't run because a prerequisite
 * isn't satisfied — for cluster resume, this means the workspace itself is
 * suspended and must be resumed first.
 */
export const PREREQUISITE_NOT_MET = 'PREREQUISITE_NOT_MET';

interface ResumeErrorData {
  error?: string;
  code?: string;
  message?: string;
  result?: string;
  detail?: string;
  details?: { details?: string; namespace?: string };
}

/**
 * Extracts the actionable message from a failed cluster-resume response so the
 * editor surfaces the real reason instead of a generic "Cluster resume failed."
 *
 * Special-cases PREREQUISITE_NOT_MET (the workspace is suspended) to guide the
 * user to resume the workspace first. Returns null when the error carries no
 * usable body (e.g. a network error), letting callers fall back to their own
 * offline / generic handling.
 */
export const getClusterResumeErrorMessage = (
  error: { response?: { data?: ResumeErrorData } } | undefined
): string | null => {
  const data = error?.response?.data;
  if (!data) {
    return null;
  }
  if (data.code === PREREQUISITE_NOT_MET) {
    return data.details?.details || 'Workspace is suspended. Please resume the workspace first.';
  }
  return (
    data.details?.details ||
    (typeof data.result === 'string' ? data.result : undefined) ||
    data.error ||
    data.message ||
    data.detail ||
    null
  );
};

/**
 * Stores logs in localStorage for debugging
 */
export const storeLogsInLocalStorage = (logEntry: Record<string, unknown>): void => {
  try {
    const key = 'query_editor_logs';
    const maxLogs = 100;
    const logs = JSON.parse(localStorage.getItem(key) || '[]');
    logs.push({
      ...logEntry,
      timestamp: new Date().toISOString(),
    });
    // Keep only last N logs
    if (logs.length > maxLogs) {
      logs.splice(0, logs.length - maxLogs);
    }
    localStorage.setItem(key, JSON.stringify(logs));
  } catch {
    // Silent fail - logging should not break app
  }
};

export const toastMessages: Record<string, string> = {
  'TM-WS-DISABLE':
    'This workspace has been disabled. Kindly enable it to proceed with your requests.',
  'TM-SESSION_EXPIRY':
    'Your current session has expired. Kindly log in again to proceed using e6data.',
  'TM-NO-ACCESS': 'You are not authorised to execute this request.',
  'TM-502': "The server couldn't complete your request. Please try again later.",
  'TM-SESSION-VALID':
    'You are already logged in to e6data. We are redirecting you to your workspace to continue using our services.',
  'TM-LOGOUT':
    'You have successfully logged out of your current session. Kindly log in again to proceed using e6data.',
  'TM-SET-PW-1':
    'This link has already been used. Please generate a new one on our login page to continue using our services.',
  'TM-SET-PW-2': 'This URL does not contain the necessary token. Please try logging in.',
  'TM-SET-PW-3': 'Your password has been successfully updated. Please log in.',
  'TM-LIMIT-EXCEEDS':
    'You have exceeded the maximum number of requests allowed. Please try again after some time.',
  'TM-ONLINE': "You're back online! 🎉",
  'TM-OFFLINE': 'You are offline! ❌',
};
